// Training Center → Golden Datasets tab.
//
// Replaces web/scripts/training-center.js's geLoadTab(). Lists datasets
// per organization, kicks off runs, and shows recent runs history.
// Per-item editing is the next slice on this branch.

import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GoldenDataset, GoldenRun, OrgScope } from "../../api/training";
import { trainingApi } from "../../api/training";
import { AdminGrid, ErrorBlock, LoadingBlock, formatDateTime, formatPercent } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function GoldenDatasetsTab({ bearer, scope }: Props) {
  const [datasets, setDatasets] = useState<GoldenDataset[]>([]);
  const [runs, setRuns] = useState<GoldenRun[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const reload = useCallback(() => {
    let cancelled = false;
    Promise.all([trainingApi.goldenDatasets(bearer, scope), trainingApi.goldenRuns(bearer, {})])
      .then(([dsList, runsList]) => {
        if (cancelled) return;
        setDatasets(dsList || []);
        setRuns(runsList || []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const c = reload();
    setLoading(false);
    return c;
  }, [reload]);

  const handleRun = async (ds: GoldenDataset) => {
    setBusyId(ds.id);
    setError(null);
    try {
      await trainingApi.goldenRunStart(bearer, { dataset_id: ds.id });
      // Poll for completion every 5s for up to 5 min.
      let attempts = 0;
      const tick = async () => {
        attempts++;
        const r = await trainingApi
          .goldenRuns(bearer, { dataset_id: ds.id })
          .then((d) => d?.[0])
          .catch(() => null);
        if (r && r.status !== "queued" && r.status !== "in_progress") {
          reload();
          setBusyId(null);
          return;
        }
        if (attempts < 60) setTimeout(tick, 5000);
        else setBusyId(null);
      };
      setTimeout(tick, 5000);
    } catch (e) {
      setError(e as Error);
      setBusyId(null);
    }
  };

  const datasetColDefs = useMemo<ColDef<GoldenDataset>[]>(
    () => [
      { field: "organization", headerName: "Organization", flex: 1 },
      { field: "name", headerName: "Name", flex: 2 },
      { field: "item_count", headerName: "Items", width: 100, type: "numericColumn" },
      {
        field: "last_pass_rate",
        headerName: "Last Pass Rate",
        width: 150,
        type: "numericColumn",
        cellRenderer: (p: { value: number | null }) =>
          p.value != null ? <PassRate value={p.value} /> : <span className="text-neutral-400">—</span>,
      },
      {
        field: "last_run_at",
        headerName: "Last Run",
        flex: 1,
        sort: "desc",
        valueFormatter: (p) => (p.value ? formatDateTime(p.value as string) : "—"),
      },
      {
        headerName: "",
        width: 110,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (p: { data: GoldenDataset }) => (
          <button
            type="button"
            onClick={() => handleRun(p.data)}
            disabled={busyId === p.data.id}
            className="rounded-md bg-brand text-white px-3 h-7 text-xs font-medium hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busyId === p.data.id ? "Running…" : "Run"}
          </button>
        ),
      },
    ],
    [busyId],
  );

  const runColDefs = useMemo<ColDef<GoldenRun>[]>(
    () => [
      {
        field: "created_at",
        headerName: "Started",
        flex: 1,
        sort: "desc",
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      { field: "dataset_id", headerName: "Dataset", width: 110, type: "numericColumn" },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        cellRenderer: (p: { value: GoldenRun["status"] }) => <RunStatusBadge status={p.value} />,
      },
      {
        field: "pass_rate",
        headerName: "Pass Rate",
        width: 130,
        type: "numericColumn",
        cellRenderer: (p: { value: number | null }) =>
          p.value != null ? <PassRate value={p.value} /> : <span className="text-neutral-400">—</span>,
      },
      {
        headerName: "Passed / Failed",
        width: 160,
        type: "numericColumn",
        valueGetter: (p) => `${p.data?.passed ?? 0} / ${p.data?.failed ?? 0}`,
      },
    ],
    [],
  );

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      {error && <ErrorBlock error={error} />}

      <div>
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">Datasets</h3>
        {datasets.length === 0 ? (
          <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
            No golden datasets for the selected organization.
          </div>
        ) : (
          <AdminGrid<GoldenDataset> rowData={datasets} columnDefs={datasetColDefs} />
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">Recent Runs</h3>
        {runs.length === 0 ? (
          <div className="text-sm text-neutral-500">No runs yet.</div>
        ) : (
          <AdminGrid<GoldenRun> rowData={runs.slice(0, 25)} columnDefs={runColDefs} />
        )}
      </div>
    </div>
  );
}

function PassRate({ value }: { value: number }) {
  const pct = formatPercent(value, 0);
  const color =
    value >= 0.85 ? "text-emerald-700" : value >= 0.6 ? "text-amber-700" : "text-red-700";
  return <span className={`font-semibold ${color}`}>{pct}</span>;
}

function RunStatusBadge({ status }: { status: GoldenRun["status"] }) {
  const cls =
    status === "completed"
      ? "bg-emerald-50 text-emerald-700"
      : status === "in_progress" || status === "queued"
        ? "bg-blue-50 text-blue-700"
        : "bg-red-50 text-red-700";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {status}
    </span>
  );
}
